import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthState } from '@fitscovery/auth/data-access';
import { HttpClientService } from '@fitscovery/partner-web/shared/services/http';
import { Guid } from '@fitscovery/common/types';
import { SESSION_STORAGE, WINDOW } from '@ng-web-apis/common';
import { Select } from '@ngxs/store';
import { environment } from 'apps/partner-web/src/environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PaymentFor } from '../enums';
import { AttachToPaymentIntentRequest, CreatePaymentIntentRequest, CreatePaymentIntentResponse, CreatePaymentRequest, CreatePaymentResult, CreatePaymentTokenRequest, CreatePaymentTokenResponse, CreditCard, PaymentIntent, PaymentIntentResponse, PaymentMethodRequest, PaymentMethodResponse, PaymongoErrorResponse } from '../models';

@Injectable()
export class ApiPaymongoService {

	/* This has been deprecated today, Feb. 3, 2021. Refer to the docs here: https://developers.paymongo.com/reference#create-a-token */
	private paymentToken: string;
	private paymentType: string;
	private token: string;

	@Select(AuthState.token) userToken$: Observable<string>;

	constructor(
		@Inject(SESSION_STORAGE) private sessionStorage: Storage,
		@Inject(WINDOW) private window: any,
		private route: ActivatedRoute,
		private http: HttpClientService,
		private httpClient: HttpClient,
		private router: Router
	) {
		this.userToken$.subscribe(async (token: string) => {
			this.token = token;
		});
	}

	async createPaymentIntent(itemId: string | Guid | number, paymentFor: PaymentFor | number): Promise<CreatePaymentIntentResponse> {
		const paymentInfo: CreatePaymentIntentRequest = {
			itemId: itemId,
			paymentFor: paymentFor
		};

		return (this.http.post(`${environment.azureFunctionsApi.payments}/payments/intents`, paymentInfo, this.token) as any)
			.pipe(
				map((result: CreatePaymentIntentResponse) => {
					sessionStorage.setItem('paymentProviderIntentId', result.paymentProviderIntentId.toString());
					sessionStorage.setItem('paymentIntentId', result.paymentIntentId.toString());
					sessionStorage.setItem('clientKey', result.clientKey.toString());
					return result;
				}),
				catchError((error: any) => {
					return throwError(error);
				})
			).toPromise();
	}

	createPaymentMethod(creditCard: CreditCard): Observable<any> {
		const authorization = this.createPaymongAuthorization();
		const data: PaymentMethodRequest = {
			data: {
				attributes: {
					type: 'card',
					details: {
						card_number: creditCard.number,
						exp_month: creditCard.expirationMonth,
						exp_year: creditCard.expirationYear,
						cvc: creditCard.cvc,
					},
					billing: {
						name: creditCard.holderName,
						email: creditCard.email,
						phone: creditCard.phone
					}
				}
			}
		};

		return (this.httpClient.post(`${environment.paymentsApi.paymongo}/payment_methods`, data, { headers: { 'Authorization': authorization } }) as any)
			.pipe(
				map((response: PaymentMethodResponse) => {
					return response.data;
				}),
				catchError((errorResponse: PaymongoErrorResponse) => {
					return throwError(errorResponse.error.errors[0]);
				})
			);
	}

	attachToPaymentIntent(paymentMethodId: string): Observable<any> {
		const authorization: string = this.createPaymongAuthorization();
		const paymentIntentId = this.sessionStorage.getItem('clientKey')!.split('_client')[0];
		const redirectUrl: string = this.window.location.href;
		const data: AttachToPaymentIntentRequest = {
			data: {
				attributes: {
					payment_method: paymentMethodId,
					client_key: this.sessionStorage.getItem('clientKey')!,
					return_url: redirectUrl
				}
			}
		};

		return (this.httpClient.post(`${environment.paymentsApi.paymongo}/payment_intents/${paymentIntentId}/attach`, data, { headers: { 'Authorization': authorization, 'Content-Type': 'application/json' } }) as any)
			.pipe(
				map((response: PaymentIntentResponse) => {
					return response.data;
				}),
				catchError((errorResponse: PaymongoErrorResponse) => {
					return throwError(errorResponse.error.errors[0]);
				})
			);
	}

	getPaymentIntent(paymentIntent: string, clientKey: string): Observable<PaymentIntent> {
		const options = {
			headers: { Authorization: this.createPaymongAuthorization() }
		};

		return (this.httpClient.get(`${environment.paymentsApi.paymongo}/payment_intents/${paymentIntent}?client_key=${clientKey}`, options) as any)
			.pipe(
				map((response: PaymentIntentResponse) => {
					this.sessionStorage.removeItem('clientKey');
					this.sessionStorage.setItem('clientKey', response.data.attributes.client_key);
					return response.data;
				}),
				catchError((errorResponse: PaymongoErrorResponse) => {
					return throwError(errorResponse.error.errors[0]);
				})
			);
	}

	// Refactor return 'any' object into a strongly typed one.
	updatePaymentResult(paymentIntentId: Guid | string): Promise<any> {
		return this.http.put(`${environment.azureFunctionsApi.payments}/payments/intents/${paymentIntentId}`, null, this.token)
			.pipe(
				map((response: any) => {
					return response;
				}),
				catchError((error: any) => {
					return throwError(error.errors[0]);
				})
			).toPromise();

	}

	/* This has been deprecated today, Feb. 3, 2021. Refer to the docs here: https://developers.paymongo.com/reference#create-a-token */

	createToken(creditCard: CreditCard): Promise<void> {
		const authorization = this.createPaymongAuthorization();
		const body = <CreatePaymentTokenRequest>{
			data: {
				attributes: {
					number: creditCard.number.replace(/\s/g, ''),
					exp_month: creditCard.expirationMonth,
					exp_year: creditCard.expirationYear,
					cvc: creditCard.cvc,
					billing: {
						name: creditCard.holderName,
						email: creditCard.email
					}
				}
			}
		};

		return (this.http.post(`${environment.paymentsApi.paymongo}/tokens`, body, authorization) as any).pipe(
			map((token: CreatePaymentTokenResponse) => {
				this.paymentToken = token.data.id;
				this.paymentType = token.data.type;
			})
		).toPromise();

	}



	async pay(itemId: string | Guid | number, paymentFor: string | PaymentFor | number): Promise<CreatePaymentResult> {
		let route: string;

		if (paymentFor === PaymentFor.Events || paymentFor === PaymentFor[PaymentFor.Events] || paymentFor === '1') {
			route = `payments/events/${itemId}`;
		} else if (paymentFor === PaymentFor.Booking || paymentFor === PaymentFor[PaymentFor.Booking] || paymentFor === '7') {
			route = `payments/events/${itemId}`;
		} else if (paymentFor === PaymentFor.Membership || paymentFor === PaymentFor[PaymentFor.Membership] || paymentFor === '4') {
			route = `payments/partner-membership-subscription/${itemId}`;
		} else {
			throw new Error('Provided payment for doesn\'t correspond to a route.');
		}

		if (!this.paymentToken || !this.paymentType) {
			this.router.navigate(['../create'], { relativeTo: this.route });
		}

		const paymentInfo: CreatePaymentRequest = <CreatePaymentRequest>{
			itemId: itemId,
			paymentFor: paymentFor,
			tokenId: this.paymentToken,
			type: this.paymentType
		};

		return (this.http.post(`${environment.azureFunctionsApi.payments}/${route}`, paymentInfo, this.token) as any).pipe(
			map((result: CreatePaymentResult) => {
				return result;
			})
		).toPromise();
	}

	private createPaymongAuthorization(): string {
		const convertedValue = btoa(environment.paymentsApi.paymongoKey);
		const authorization = `Basic ${convertedValue}`;
		return authorization;
	}
}
